import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { router } from "@/router";
import "@/i18n";
import "@/styles/index.css";

// ── Global error boundary ────────────────────────────────────────────────────
// Prevents the entire app from white-screening when a single page component
// throws during render. React requires a class component for this.
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ProProp] Render error caught by boundary:", error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: "2rem", textAlign: "center", fontFamily: "sans-serif" }}>
          <h2 style={{ color: "#ef4444", marginBottom: "0.5rem" }}>Something went wrong</h2>
          <p style={{ color: "#6b7280", fontSize: "0.9rem" }}>
            An unexpected error occurred. Please refresh the page to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: "1rem", padding: "0.5rem 1.25rem", cursor: "pointer", borderRadius: "6px", border: "1px solid #d1d5db" }}
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Global unhandled-rejection handler ───────────────────────────────────────
// Surfaces stray promise rejections (API failures, stream errors) to the
// console so they're visible in Sentry / browser devtools instead of silently
// disappearing.
window.addEventListener("unhandledrejection", (event) => {
  console.error("[ProProp] Unhandled promise rejection:", event.reason);
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </React.StrictMode>,
);
